import { useEffect } from '@frontend/react';
import { LoadingSpinnerPanel, MantineProvider, ModalsProvider, Notifications } from '@frontend/web-react';
import { identifyClient } from '@frontend/web-utils';
import { RouterProvider } from '@frontend/web/components';
import { useAuthentication, useLazyRetrieveClientQuery } from '@frontend/web/hooks';
import { mantineTheme } from '@frontend/web/mantine';
import { persistor, store } from '@frontend/web/redux/store';
import { router } from '@frontend/web/router';
import { Env } from '@frontend/web/utils';
import { GrowthBookProvider } from '@growthbook/growthbook-react';
// Core always needs to be first
import { useAuthSlice } from '@frontend/redux';
import '@mantine/carousel/styles.css';
import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/dropzone/styles.css';
import '@mantine/notifications/styles.css';
import '@mantine/spotlight/styles.css';
import { Suspense, useState } from 'react';
import ReactDOM from 'react-dom/client';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { initApp } from './utils/initApp';

const { growthbook, segmentClient } = initApp();

// Needs to be wrapped inside Redux Provider/PersistGate before useAuthentication is accessible
export const Router = () => {
  const authentication = useAuthentication();

  return (
    <Suspense fallback={<div />}>
      <RouterProvider context={{ authentication }} router={router} />
    </Suspense>
  );
};

export const App = () => {
  const [retrieveClient, { isLoading: isClientLoading }] = useLazyRetrieveClientQuery();
  const { client: reduxClient, storeClient, token } = useAuthSlice();
  const [isIdentifyDone, setIsIdentifyDone] = useState(false);

  useEffect(() => {
    if (token) {
      (async () => {
        await retrieveClient().then(
          ({ data: client }) => {
            if (client) {
              storeClient(client);
            }

            // At runtime the client isn't immediately available and causes sentry errors
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            if (client || reduxClient) {
              identifyClient(client ?? reduxClient!, Env.VITE_ENV);
              setIsIdentifyDone(true);
            } else {
              setIsIdentifyDone(false);
            }
          },
          () => setIsIdentifyDone(true),
        );
      })();
    } else {
      setIsIdentifyDone(false);
      segmentClient?.ready(() => {
        growthbook.setAttributes({
          anonymousId: segmentClient.user().then((user) => user.anonymousId),
        });
      });
    }
    growthbook.refreshFeatures();
  }, [token]);

  // We want to wait until the client is identified with Growthbook, if they're logged in
  const isAppReadyToRender = !isClientLoading && (token ? isIdentifyDone : true);

  return isAppReadyToRender ? <Router /> : <LoadingSpinnerPanel className="h-screen w-full pb-xxxl" />;
};

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
const rootElement = document.getElementById('root')!;
if (!rootElement?.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <MantineProvider theme={mantineTheme}>
      <div className="min-h-screen">
        <ModalsProvider>
          <Notifications position="bottom-left" />
          <Provider store={store}>
            <PersistGate loading={null} persistor={persistor}>
              <GrowthBookProvider growthbook={growthbook}>
                <App />
              </GrowthBookProvider>
            </PersistGate>
          </Provider>
          {/* <TanStackRouterDevtools initialIsOpen={false} router={router} /> */}
        </ModalsProvider>
      </div>
    </MantineProvider>,
  );
}
